import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

const DB_NAME = 'rni-images';
const DB_VERSION = 1;
const IMAGES_STORE = 'images';
const BLOBS_STORE = 'blobs';
const INDEX_BY_DIR = 'by-dir';
const INDEX_BY_STATUS = 'by-status';
const INDEX_BY_UPDATED_AT = 'by-updatedAt';

export type ImageStatus = 'ready' | 'error';

export interface ImageMeta {
  id: string;
  dir: string;
  filename: string;
  size: number;
  contentType: string;
  lastModified: string; // ISO
  etag?: string | null;
  status: ImageStatus;
  addedAt: number;
  updatedAt: number;
}

export interface ApiImageResponse {
  images: Record<string, {
    filename: string;
    size: number;
    lastModified: string;
    contentType: string;
    url: string;
  }[]>;
}

export class ImageFetchError extends Error {
  constructor(public imageId: string, public status: number, message: string) {
    super(message);
    this.name = 'ImageFetchError';
  }
}

interface ImageBlob {
  id: string;
  size: number;
  blob: Blob;
  storedAt: number;
}

interface ImagesDB extends DBSchema {
  [IMAGES_STORE]: {
    key: string;
    value: ImageMeta;
    indexes: {
      [INDEX_BY_DIR]: string;
      [INDEX_BY_STATUS]: ImageStatus;
      [INDEX_BY_UPDATED_AT]: number;
    };
  };
  [BLOBS_STORE]: {
    key: string;
    value: ImageBlob;
  };
}

const fetchingImages = new Set<string>();

let dbPromise: Promise<IDBPDatabase<ImagesDB>> | null = null;

export function getImagesDB() {
  dbPromise ??= openDB<ImagesDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const images = db.createObjectStore(IMAGES_STORE, { keyPath: 'id' });
      images.createIndex(INDEX_BY_DIR, 'dir', { unique: false });
      images.createIndex(INDEX_BY_STATUS, 'status', { unique: false });
      images.createIndex(INDEX_BY_UPDATED_AT, 'updatedAt', { unique: false });

      db.createObjectStore(BLOBS_STORE, { keyPath: 'id' });
    },
  });

  return dbPromise;
}

export async function downloadAndStoreImages(resp: ApiImageResponse) {
  const results = [];

  for (const [ dir, files ] of Object.entries(resp.images)) {
    for (const file of files) {
      const id = `${dir}${file.filename}`;

      const existing = await getImageMeta(id);
      if (existing
        && existing.lastModified === file.lastModified
        && existing.size === file.size) {
        results.push({ id, status: 'skipped', existing: true });
        continue;
      }

      try {
        const result = await downloadAndStoreImage(id, dir, file);
        results.push(result);
      }
      catch (error) {
        console.error(`Failed to download image ${id}:`, error);
        results.push({
          id,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }
  }

  return results;
}

export async function downloadAndStoreImage(
  id: string,
  dir: string,
  file: { filename: string; size: number; lastModified: string; contentType: string; url: string },
) {
  const existing = await getImageMeta(id);
  if (existing
    && existing.lastModified === file.lastModified
    && existing.size === file.size) {
    return { id, status: 'skipped' as const, size: file.size };
  }

  if (fetchingImages.has(id)) {
    throw new Error(`Image ${id} is already being downloaded`);
  }

  fetchingImages.add(id);

  try {
    const response = await fetch(file.url, { cache: 'no-store' });

    if (!response.ok) {
      throw new ImageFetchError(id, response.status, `HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();
    const etag = response.headers.get('ETag');
    const now = Date.now();

    // Store both metadata and blob in a transaction
    const db = await getImagesDB();
    const transaction = db.transaction([ IMAGES_STORE, BLOBS_STORE ], 'readwrite');

    const meta: ImageMeta = {
      id,
      dir,
      filename: file.filename,
      size: file.size,
      contentType: file.contentType,
      lastModified: file.lastModified,
      etag,
      status: 'ready',
      addedAt: now,
      updatedAt: now,
    };

    const blobRecord: ImageBlob = {
      id,
      size: blob.size,
      blob,
      storedAt: now,
    };

    await Promise.all([
      transaction.objectStore(IMAGES_STORE).put(meta),
      transaction.objectStore(BLOBS_STORE).put(blobRecord),
    ]);

    await transaction.done;

    return { id, status: 'success', size: blob.size };
  }
  finally {
    fetchingImages.delete(id);
  }
}

export async function putBlob(id: string, blob: Blob, etag?: string | null) {
  const db = await getImagesDB();
  const transaction = db.transaction([ BLOBS_STORE, IMAGES_STORE ], 'readwrite');
  const now = Date.now();

  await transaction.objectStore(BLOBS_STORE).put({ id, size: blob.size, blob, storedAt: now });

  const meta = await transaction.objectStore(IMAGES_STORE).get(id);
  if (meta) {
    meta.status = 'ready';
    meta.updatedAt = now;
    if (etag !== undefined) meta.etag = etag;
    await transaction.objectStore(IMAGES_STORE).put(meta);
  }
  await transaction.done;
}

export async function getImageMeta(id: string): Promise<ImageMeta | null> {
  const db = await getImagesDB();
  return (await db.get(IMAGES_STORE, id)) ?? null;
}

export async function getObjectUrl(id: string): Promise<{ url: string; revoke: () => void } | null> {
  const db = await getImagesDB();
  const transaction = db.transaction([ BLOBS_STORE, IMAGES_STORE ], 'readonly');
  const rec = await transaction.objectStore(BLOBS_STORE).get(id);
  if (!rec) return null;

  const url = URL.createObjectURL(rec.blob);
  const revoke = () => {
    URL.revokeObjectURL(url);
  };

  return { url, revoke };
}

export async function listByDir(dir: string) {
  const db = await getImagesDB();
  return db.getAllFromIndex(IMAGES_STORE, INDEX_BY_DIR, dir);
}

export async function getNumberOfImages(dir: string) {
  const db = await getImagesDB();
  return db.countFromIndex(IMAGES_STORE, INDEX_BY_DIR, dir);
}

export async function removeOldImages(maxAgeMs: number = 7 * 24 * 60 * 60 * 1000) {
  const db = await getImagesDB();
  const transaction = db.transaction([ IMAGES_STORE, BLOBS_STORE ], 'readwrite');
  const cutoff = Date.now() - maxAgeMs;

  const allImages = await transaction.objectStore(IMAGES_STORE).getAll();
  const toDelete = allImages.filter(meta =>
    meta.updatedAt < cutoff,
  );

  for (const meta of toDelete) {
    await transaction.objectStore(IMAGES_STORE).delete(meta.id);
    await transaction.objectStore(BLOBS_STORE).delete(meta.id);
  }

  await transaction.done;
  return toDelete.length;
}

export async function getStorageStats() {
  const db = await getImagesDB();
  const [ images, blobs ] = await Promise.all([
    db.getAll(IMAGES_STORE),
    db.getAll(BLOBS_STORE),
  ]);

  return {
    imageCount: images.length,
    totalSize: blobs.reduce((sum, blob) => sum + blob.size, 0),
    statusBreakdown: images.reduce((acc, img) => {
      acc[img.status] = (acc[img.status] || 0) + 1;
      return acc;
    }, {} as Record<ImageStatus, number>),
    oldestImage: images.reduce<ImageMeta | null>((oldest, img) =>
      !oldest || img.addedAt < oldest.addedAt ? img : oldest,
    null),
    newestImage: images.reduce<ImageMeta | null>((newest, img) =>
      !newest || img.addedAt > newest.addedAt ? img : newest,
    null),
  };
}

export async function deleteImages(ids: string[]) {
  const db = await getImagesDB();
  const transaction = db.transaction([ IMAGES_STORE, BLOBS_STORE ], 'readwrite');

  for (const id of ids) {
    await transaction.objectStore(IMAGES_STORE).delete(id);
    await transaction.objectStore(BLOBS_STORE).delete(id);
  }

  await transaction.done;
}
